import React from 'react';
import { HAIRLINE, INSET, MUTED } from './tokens';
import { money } from '../../utils/surfaceFormat';
import { useSoft, SOFT } from './SurfaceStyleContext';

/**
 * The PR fee, GST and Stripe processing math the checkout quote uses (CheckoutService.GetQuote),
 * for live client previews before the server quote lands. GST is 5% of tickets + PR fee; Stripe is a
 * gross-up so the guest covers the 2.9% + 30¢: gross = ceil((pre + 30) / (1 − 0.029)), fee = gross − pre.
 */
export function computeBreakdown({ unitCents = 0, qty = 1, feeCents = 100, gstPercent = 5, stripePercent = 2.9, stripeFlatCents = 30, walletCents = 0 }) {
  const tickets = unitCents * qty;
  const fee = unitCents > 0 ? feeCents : 0;
  const gst = Math.round((tickets + fee) * (gstPercent / 100));
  const pre = tickets + fee + gst;
  const cardPre = Math.max(0, pre - Math.max(0, walletCents));
  const proc = cardPre > 0 ? Math.ceil((cardPre + stripeFlatCents) / (1 - stripePercent / 100)) - cardPre : 0;
  return { tickets, fee, gst, proc, total: pre + proc, walletApplied: Math.min(pre, Math.max(0, walletCents)) };
}

/**
 * Inset money block: lines, a bordered TOTAL row, and an optional `WALLET APPLIED FIRST −$X`
 * hairline row. lines: [{ k, v }] with v already formatted.
 */
export default function MoneyBreakdown({ lines = [], totalCents, totalLabel = 'TOTAL', walletAppliedCents = 0, walletLabel = 'WALLET APPLIED FIRST', after, className = '', testId = 'money-breakdown' }) {
  const soft = useSoft();
  if (soft) {
    // Soft: 11px rows over a #F2F2F2 rule, the total on a 2px black rule, no box.
    return (
      <div className={`flex flex-col ${className}`} style={{ gap: 7, paddingTop: 12, borderTop: `2px solid ${SOFT.chip}` }} data-testid={testId} data-soft="1">
        {lines.map((l, i) => (
          <div key={`${l.k}-${i}`} className="flex justify-between gap-3 font-mono" style={{ fontSize: 11, letterSpacing: '0.06em' }}>
            <span style={{ color: MUTED }}>{l.k}</span>
            <span>{l.v}</span>
          </div>
        ))}
        {totalCents !== undefined && (
          <div className="flex justify-between gap-3 font-mono" style={{ paddingTop: 8, borderTop: '2px solid #000', fontSize: 13, fontWeight: 800, letterSpacing: '0.06em' }}>
            <span className="uppercase">{totalLabel}</span>
            <span>{money(totalCents)}</span>
          </div>
        )}
        {walletAppliedCents > 0 && (
          <div className="flex justify-between gap-3 font-mono" style={{ fontSize: 11, color: MUTED, letterSpacing: '0.06em' }}>
            <span>{walletLabel}</span>
            <span>{money(-walletAppliedCents)}</span>
          </div>
        )}
        {after}
      </div>
    );
  }
  return (
    <div className={`border-2 border-black ${className}`} style={{ background: INSET }} data-testid={testId}>
      {lines.map((l, i) => (
        <div key={`${l.k}-${i}`} className="flex justify-between gap-3 font-mono" style={{ padding: '7px 12px', fontSize: 12 }}>
          <span style={{ color: MUTED }}>{l.k}</span>
          <span className="font-bold">{l.v}</span>
        </div>
      ))}
      {totalCents !== undefined && (
        <div className="flex justify-between gap-3 border-t-2 border-black font-mono" style={{ padding: '10px 12px', fontSize: 14 }}>
          <span className="font-bold uppercase" style={{ letterSpacing: '0.05em' }}>{totalLabel}</span>
          <span className="font-bold">{money(totalCents)}</span>
        </div>
      )}
      {walletAppliedCents > 0 && (
        <div className="flex justify-between gap-3 font-mono" style={{ padding: '7px 12px', borderTop: `2px solid ${HAIRLINE}`, fontSize: 11, color: MUTED }}>
          <span>{walletLabel}</span>
          <span>{money(-walletAppliedCents)}</span>
        </div>
      )}
      {after}
    </div>
  );
}
